import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, LogOut, FileText, Image, Upload, Brain } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface FAQ {
  id: string;
  question: string;
  answer: string;
  is_active: boolean;
  embedding: any;
  created_at: string;
  updated_at: string;
}

const FAQAdminPage = () => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [isActive, setIsActive] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleLogout = async () => {
    await signOut();
    toast({ title: "Logged out", description: "You have been logged out successfully." });
    navigate("/");
  };

  const { data: faqs, isLoading } = useQuery({
    queryKey: ["chatbot-faqs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chatbot_faqs")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as FAQ[];
    },
  });

  const generateEmbeddings = async (faqIds: string[]) => {
    try {
      const { data, error } = await supabase.functions.invoke("generate-faq-embedding", {
        body: { faq_ids: faqIds },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    } catch (err: any) {
      console.error("Embedding generation failed:", err);
      toast({
        title: "Embedding Warning",
        description: `FAQ saved but embedding generation failed: ${err.message}`,
        variant: "destructive",
      });
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (faq: { id?: string; question: string; answer: string; is_active: boolean }) => {
      if (faq.id) {
        const { data, error } = await supabase
          .from("chatbot_faqs")
          .update({ question: faq.question, answer: faq.answer, is_active: faq.is_active })
          .eq("id", faq.id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from("chatbot_faqs")
          .insert({ question: faq.question, answer: faq.answer, is_active: faq.is_active })
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["chatbot-faqs"] });
      toast({ title: "Success", description: "FAQ saved! Generating embedding..." });
      resetForm();
      await generateEmbeddings([data.id]);
      queryClient.invalidateQueries({ queryKey: ["chatbot-faqs"] });
      toast({ title: "Done", description: "Embedding generated successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("chatbot_faqs").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatbot-faqs"] });
      toast({ title: "Deleted", description: "FAQ deleted successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    setQuestion("");
    setAnswer("");
    setIsActive(true);
    setEditingFaq(null);
    setIsCreateOpen(false);
  };

  const openEdit = (faq: FAQ) => {
    setEditingFaq(faq);
    setQuestion(faq.question);
    setAnswer(faq.answer);
    setIsActive(faq.is_active);
  };

  const handleSubmit = () => {
    if (!question.trim() || !answer.trim()) {
      toast({ title: "Validation", description: "Question and answer are required.", variant: "destructive" });
      return;
    }
    saveMutation.mutate({ id: editingFaq?.id, question, answer, is_active: isActive });
  };

  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const lines = text.split("\n").filter((l) => l.trim());

    // Skip header row if it looks like one
    const startIdx = lines[0]?.toLowerCase().includes("question") ? 1 : 0;
    const rows: { question: string; answer: string; is_active: boolean }[] = [];

    for (let i = startIdx; i < lines.length; i++) {
      // Handle quoted CSV fields
      const match = lines[i].match(/^"?([^"]*)"?\s*,\s*"?([^"]*)"?\s*$/);
      if (match) {
        rows.push({ question: match[1].trim(), answer: match[2].trim(), is_active: true });
      } else {
        const parts = lines[i].split(",");
        if (parts.length >= 2) {
          rows.push({
            question: parts[0].trim().replace(/^"|"$/g, ""),
            answer: parts.slice(1).join(",").trim().replace(/^"|"$/g, ""),
            is_active: true,
          });
        }
      }
    }

    if (rows.length === 0) {
      toast({ title: "Error", description: "No valid rows found in CSV.", variant: "destructive" });
      return;
    }

    toast({ title: "Importing", description: `Importing ${rows.length} FAQs...` });

    const { data, error } = await supabase
      .from("chatbot_faqs")
      .insert(rows)
      .select("id");

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }

    queryClient.invalidateQueries({ queryKey: ["chatbot-faqs"] });
    toast({ title: "Imported", description: `${data.length} FAQs imported. Generating embeddings...` });

    await generateEmbeddings(data.map((d) => d.id));
    queryClient.invalidateQueries({ queryKey: ["chatbot-faqs"] });
    toast({ title: "Done", description: "All embeddings generated." });

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const faqFormContent = (
    <div className="space-y-4">
      <div>
        <Label htmlFor="question">Question</Label>
        <Input id="question" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. How much does pressure washing cost?" />
      </div>
      <div>
        <Label htmlFor="answer">Answer</Label>
        <Textarea id="answer" value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Enter the answer..." rows={5} />
      </div>
      <div className="flex items-center gap-2">
        <Switch id="is_active" checked={isActive} onCheckedChange={setIsActive} />
        <Label htmlFor="is_active">Active</Label>
      </div>
      <Button onClick={handleSubmit} disabled={saveMutation.isPending} className="w-full">
        {saveMutation.isPending ? "Saving..." : editingFaq ? "Update FAQ" : "Add FAQ"}
      </Button>
    </div>
  );

  return (
    <>
      <SEO title="FAQ Knowledge Base | Admin" path="/admin/faqs" noindex />

      <Navbar />

      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4 py-24">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl font-bold">FAQ Knowledge Base</h1>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => navigate("/admin/blog")}>
                <FileText className="w-4 h-4 mr-2" />
                Blog
              </Button>
              <Button variant="outline" onClick={() => navigate("/admin/media")}>
                <Image className="w-4 h-4 mr-2" />
                Media
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Log Out
              </Button>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleCSVUpload}
              />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>

              <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Add FAQ
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add New FAQ</DialogTitle>
                  </DialogHeader>
                  {faqFormContent}
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading FAQs...</p>
            </div>
          ) : faqs && faqs.length > 0 ? (
            <div className="bg-card rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[30%]">Question</TableHead>
                    <TableHead className="w-[40%]">Answer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Embedding</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {faqs.map((faq) => (
                    <TableRow key={faq.id}>
                      <TableCell className="font-medium">{faq.question}</TableCell>
                      <TableCell className="text-sm text-muted-foreground truncate max-w-xs">
                        {faq.answer.length > 100 ? faq.answer.slice(0, 100) + "..." : faq.answer}
                      </TableCell>
                      <TableCell>
                        <Badge variant={faq.is_active ? "default" : "secondary"}>
                          {faq.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {faq.embedding ? (
                          <Brain className="w-4 h-4 text-green-500" />
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">Missing</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" onClick={() => openEdit(faq)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg">
                              <DialogHeader>
                                <DialogTitle>Edit FAQ</DialogTitle>
                              </DialogHeader>
                              {faqFormContent}
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMutation.mutate(faq.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 bg-card rounded-lg border">
              <p className="text-muted-foreground mb-4">No FAQs yet. Add your first one!</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => setIsCreateOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First FAQ
                </Button>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default FAQAdminPage;
